import { Component, OnInit } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import { Warehouse } from '../../models/warehouse.model';
import { ReconciliationRow, ReconciliationResult } from '../../models/warehouse-reporting.model';
import { WarehouseService } from '../../services/warehouse.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { ReportCsvService } from '../../services/report-csv.service';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';

/**
 * Stored stock level against the level recomputed as SUM(signed qty) over the ledger.
 *
 * Any variance is a defect by definition — the level is not an independent number, it is
 * a cache of the ledger. This screen therefore leads with the variances and treats a
 * clean run as the unremarkable case.
 */
@Component({
    selector: 'app-report-reconciliation',
    templateUrl: './report-reconciliation.component.html',
    styleUrls: ['./report-reconciliation.component.scss'],
    standalone: false
})
export class ReportReconciliationComponent implements OnInit {

  warehouses: Warehouse[] = [];

  selectedWarehouseId: number = -1;

  rows: ReconciliationRow[] = [];

  /** Pairs the backend examined, whether or not they agreed. */
  checked: number = 0;

  /** True when every pair agreed. Only meaningful once hasRun and not loadFailed. */
  balanced: boolean = false;

  hasRun: boolean = false;
  running: boolean = false;

  /** Set when the run failed, so a clean-looking empty table is never mistaken for a pass. */
  loadFailed: boolean = false;
  loadError: string = '';

  constructor(private warehouseReportingService: WarehouseReportingService,
    private warehouseService: WarehouseService,
    private stockQuantityService: StockQuantityService,
    private reportCsvService: ReportCsvService) { }

  ngOnInit(): void {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert('Cannot load warehouses');
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })
  }

  onWarehouseChanged(warehouseId: any) {
    this.selectedWarehouseId = +warehouseId;
  }

  loadReport() {
    this.running = true;
    this.loadFailed = false;
    this.loadError = '';

    this.warehouseReportingService.getReconciliation(this.selectedWarehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      this.running = false;
      this.loadFailed = true;
      this.loadError = e && e.status ? 'The server answered ' + e.status + '.' : '';
      this.rows = [];
      this.hasRun = false;
      return e;
    }))
    .subscribe((result: ReconciliationResult) => {
      this.rows = result && result.discrepancies ? result.discrepancies : [];
      this.checked = result ? result.checked : 0;
      this.balanced = result ? result.balanced : false;
      this.hasRun = true;
      this.running = false;
    })
  }

  /**
   * Every returned row is a discrepancy — the endpoint is variance-only and has already
   * applied its own tolerance. So there is no client-side filter and, importantly, no
   * client-side re-derivation: the backend tolerance is scaled by the location's ledger
   * throughput (measured around 6.05e-05, against the flat 5e-05 this UI's rounding gives),
   * so deciding it here would disagree with the ledger and report false defects.
   */
  get visibleRows(): ReconciliationRow[] {
    return this.rows;
  }

  get varianceCount(): number {
    return this.rows.length;
  }

  /** A pair the ledger has movements for but which has no stock level row at all. */
  isMissingLevel(row: ReconciliationRow): boolean {
    return row.reason === 'missing-level';
  }

  /** A level row with nothing in the ledger behind it — stock that was never posted. */
  isMissingLedger(row: ReconciliationRow): boolean {
    return row.reason === 'orphan-level';
  }

  isNegative(qty: number): boolean {
    return this.stockQuantityService.isNegative(qty);
  }

  /** Renders null as an explicit marker rather than as 0, which would look reconciled. */
  formatQty(qty: number): string {
    if (qty === null || qty === undefined) {
      return '—';
    }

    return this.stockQuantityService.round(qty).toString();
  }

  exportToCSV() {
    const headers: string[] = ['Product', 'SKU', 'Warehouse', 'Aisle', 'Shelf',
      'Level Qty', 'Ledger Qty', 'Variance', 'Movements', 'Issue'];

    const rows: any[][] = this.visibleRows.map((row: ReconciliationRow) => [
      row.productName,
      row.sku,
      row.warehouseName,
      row.aisle,
      row.shelf,
      this.formatQty(row.levelQty),
      this.formatQty(row.ledgerQty),
      this.formatQty(row.difference),
      row.movements,
      this.issueOf(row)
    ]);

    this.reportCsvService.download('stock-reconciliation', headers, rows);
  }

  /** Plain English for the backend's `reason` discriminator. */
  issueOf(row: ReconciliationRow): string {
    if (row.reason === 'missing-level') {
      return 'No stock level row for a product and location the ledger has movements for';
    }

    if (row.reason === 'orphan-level') {
      return 'Stock level with no movements behind it';
    }

    if (row.reason === 'diverged') {
      return 'Level disagrees with the ledger';
    }

    return row.reason;
  }
}
